import React, { useEffect, useMemo } from "react";
import { Panel } from "primereact/panel";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { FloatLabel } from "primereact/floatlabel";
import { useTenantScope } from "../../../hooks/useTenantScope";
import { useGetEventTypesQuery } from "../../../store/services/events-types";
import { useGetTenantsQuery } from "../../../store/services/tenants";
import {
  findGrupsTenantIdFromList,
  fromDropdownTenantId,
  ownedRulesTenantIdForAdmin,
  toDropdownTenantId,
} from "../constants/rulesTenant";
import type {
  AdminRulesBaseFormValues,
  AdminRulesInlineFormField,
  AdminRulesScopeFieldsConfig,
} from "../types";

export type RulesInlineFormMode = "hidden" | "create" | "edit";

const DEFAULT_SCOPE_FIELDS: Required<AdminRulesScopeFieldsConfig> = {
  eventTypeRequired: true,
  tenantRequired: false,
  tenantPersistence: "nullable-grups-sentinel",
};

const formatFieldLabel = (label: string, required?: boolean): string =>
  required ? `${label} *` : label;

interface RulesInlineFormPanelProps<TForm extends AdminRulesBaseFormValues> {
  mode: RulesInlineFormMode;
  values: TForm;
  onChange: (next: TForm) => void;
  customFields: AdminRulesInlineFormField<TForm>[];
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  scopeFields?: AdminRulesScopeFieldsConfig;
}

export const RulesInlineFormPanel = <TForm extends AdminRulesBaseFormValues>({
  mode,
  values,
  onChange,
  customFields,
  onSubmit,
  onCancel,
  isSubmitting,
  errorMessage,
  scopeFields,
}: RulesInlineFormPanelProps<TForm>) => {
  const resolvedScope: Required<AdminRulesScopeFieldsConfig> = {
    ...DEFAULT_SCOPE_FIELDS,
    ...scopeFields,
  };
  const usesRequiredUuidTenant = resolvedScope.tenantPersistence === "required-uuid";

  const { data: eventTypes, isLoading: isLoadingEventTypes } = useGetEventTypesQuery();
  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();
  const ownedTenantId = ownedRulesTenantIdForAdmin(tenantScope.role, tenantScope.tenantId);

  const tenantOptions = React.useMemo(
    () => (Array.isArray(tenants) ? tenants : []),
    [tenants]
  );
  const grupsTenantId = useMemo(() => findGrupsTenantIdFromList(tenants), [tenants]);
  const selectedTenantId = usesRequiredUuidTenant
    ? (values.tenantId as string | undefined)
    : toDropdownTenantId(values.tenantId as string | undefined, grupsTenantId);

  useEffect(() => {
    if (!ownedTenantId || values.tenantId === ownedTenantId) {
      return;
    }

    onChange({
      ...values,
      tenantId: ownedTenantId,
    });
  }, [onChange, ownedTenantId, values]);

  const panelHeader = mode === "edit" ? "Editar regla" : mode === "create" ? "Nueva regla" : "";

  const eventTypeId =
    values.eventTypeId == null ? undefined : (values.eventTypeId as number | undefined);

  const subTypeOptions = React.useMemo(() => {
    if (!eventTypes || !eventTypeId) return [];
    const selectedType = eventTypes.find((item) => item.eventTypeId === eventTypeId);
    return selectedType?.subTypes ?? [];
  }, [eventTypes, eventTypeId]);

  const handleEventTypeChange = ({ value }: DropdownChangeEvent) => {
    onChange({
      ...values,
      eventTypeId: value ?? null,
      eventSubTypeId: null,
    });
  };

  const handleSubTypeChange = ({ value }: DropdownChangeEvent) => {
    onChange({
      ...values,
      eventSubTypeId: value ?? null,
    });
  };

  const handleTenantChange = ({ value }: DropdownChangeEvent) => {
    if (usesRequiredUuidTenant) {
      onChange({
        ...values,
        tenantId: (value as string | undefined) ?? undefined,
      });
      return;
    }

    onChange({
      ...values,
      tenantId: fromDropdownTenantId(value as string | undefined, grupsTenantId),
    });
  };

  const handleActiveChange = (nextActive: boolean) => {
    onChange({
      ...values,
      active: nextActive,
    });
  };

  const handleCustomNumberChange =
    (name: keyof TForm & string) =>
    ({ value }: InputNumberChangeEvent) => {
      onChange({
        ...values,
        [name]: value ?? undefined,
      });
    };

  const handleCustomDropdownChange =
    (name: keyof TForm & string) =>
    ({ value }: DropdownChangeEvent) => {
      onChange({
        ...values,
        [name]: value ?? undefined,
      });
    };

  if (mode === "hidden") {
    return null;
  }

  return (
    <Panel header={panelHeader} className="mb-4">
      <div className="grid row-gap-4">
        <div className="col-12 md:col-4">
          <FloatLabel className="w-full">
            <Dropdown
              id="rulesFormEventTypeId"
              value={eventTypeId}
              onChange={handleEventTypeChange}
              options={Array.isArray(eventTypes) ? eventTypes : []}
              optionLabel="eventTypeName"
              optionValue="eventTypeId"
              loading={isLoadingEventTypes}
              className="w-full"
              showClear={!resolvedScope.eventTypeRequired}
            />
            <label htmlFor="rulesFormEventTypeId">
              {formatFieldLabel("Tipo de evento", resolvedScope.eventTypeRequired)}
            </label>
          </FloatLabel>
        </div>

        <div className="col-12 md:col-4">
          <FloatLabel className="w-full">
            <Dropdown
              id="rulesFormEventSubTypeId"
              value={values.eventSubTypeId as number | undefined}
              onChange={handleSubTypeChange}
              options={subTypeOptions}
              optionLabel="eventSubTypeName"
              optionValue="eventSubTypeId"
              className="w-full"
              disabled={!eventTypeId}
              showClear
            />
            <label htmlFor="rulesFormEventSubTypeId">Subtipo</label>
          </FloatLabel>
        </div>

        {!tenantScope.hidden && !tenantScope.locked && (
          <div className="col-12 md:col-4">
            <FloatLabel className="w-full">
              <Dropdown
                id="rulesFormTenantId"
                value={selectedTenantId}
                onChange={handleTenantChange}
                options={tenantOptions}
                optionLabel="provider"
                optionValue="id"
                loading={isLoadingTenants}
                className="w-full"
              />
              <label htmlFor="rulesFormTenantId">
                {formatFieldLabel("Tenant", resolvedScope.tenantRequired)}
              </label>
            </FloatLabel>
          </div>
        )}

        {customFields.map((field) => {
          if (field.type === "number") {
            return (
              <div className="col-12 md:col-4" key={field.name}>
                <FloatLabel className="w-full">
                  <InputNumber
                    id={`rulesForm-${field.name}`}
                    value={(values[field.name] as number | undefined) ?? null}
                    onChange={handleCustomNumberChange(field.name)}
                    className="w-full"
                    inputClassName="w-full"
                    min={field.min}
                    max={field.max}
                  />
                  <label htmlFor={`rulesForm-${field.name}`}>
                    {formatFieldLabel(field.label, field.required)}
                  </label>
                </FloatLabel>
              </div>
            );
          }

          if (field.type === "dropdown") {
            const helperText = field.getHelperText?.(values);
            const baseLabel = formatFieldLabel(field.label, field.required);
            const fieldLabel = helperText ? `${baseLabel} (${helperText})` : baseLabel;
            const dropdownOptions = field.getOptions?.(values) ?? field.options ?? [];

            return (
              <div className="col-12 md:col-4" key={field.name}>
                <FloatLabel className="w-full">
                  <Dropdown
                    id={`rulesForm-${field.name}`}
                    value={(values[field.name] as number | undefined) ?? null}
                    onChange={handleCustomDropdownChange(field.name)}
                    options={dropdownOptions}
                    optionLabel={field.optionLabel ?? "label"}
                    optionValue={field.optionValue ?? "value"}
                    className="w-full"
                  />
                  <label htmlFor={`rulesForm-${field.name}`}>{fieldLabel}</label>
                </FloatLabel>
              </div>
            );
          }

          if (field.type === "switch") {
            return (
              <div className="col-12 md:col-4 flex align-items-center gap-2" key={field.name}>
                <InputSwitch
                  inputId={`rulesForm-${field.name}`}
                  checked={Boolean(values[field.name])}
                  onChange={(event) =>
                    onChange({
                      ...values,
                      [field.name]: Boolean(event.value),
                    })
                  }
                />
                <label htmlFor={`rulesForm-${field.name}`}>{field.label}</label>
              </div>
            );
          }

          return null;
        })}

        <div className="col-12 md:col-4 flex flex-column gap-2">
          <span className="text-sm text-500 line-height-1">Activar/desactivar regla</span>
          <div className="flex align-items-center gap-2">
            <InputSwitch
              inputId="rulesFormActive"
              checked={Boolean(values.active)}
              onChange={(event) => handleActiveChange(Boolean(event.value))}
            />
          </div>
        </div>

        {errorMessage && (
          <div className="col-12">
            <small className="text-red-500">{errorMessage}</small>
          </div>
        )}

        <div className="col-12 flex justify-content-end gap-2">
          <Button
            label="Cancelar"
            severity="secondary"
            outlined
            onClick={onCancel}
            disabled={isSubmitting}
          />
          <Button
            label={mode === "edit" ? "Guardar" : "Crear"}
            icon="pi pi-check"
            onClick={onSubmit}
            loading={isSubmitting}
          />
        </div>
      </div>
    </Panel>
  );
};
